import {
  PathsAdminUsersUserIdPlansGetParametersQueryStatus,
  Plan,
  PlanStatus,
  User,
} from '@/api/models';
import { Button } from '@/components/ui/Button';
import { Pagination } from '@/components/ui/Pagination';
import { formatDateWithYear } from '@/lib/dateUtils';
import { UserPlanMutationsActions } from '@/pages/users/hooks/useUserPlanMutations';
import {
  UserPlansActions,
  UserPlansState,
} from '@/pages/users/hooks/useUserPlans';
import { mdiDelete, mdiPencil, mdiPlus } from '@mdi/js';
import * as React from 'react';

// PlanStatus values: Value0 = Active (0), Value1 = Completed (1)
const PLAN_STATUS_ACTIVE = PlanStatus.Value0;
const PLAN_STATUS_COMPLETED = PlanStatus.Value1;
// Query filter values (same numeric values)
const QUERY_STATUS_ACTIVE =
  PathsAdminUsersUserIdPlansGetParametersQueryStatus.Value0;
const QUERY_STATUS_COMPLETED =
  PathsAdminUsersUserIdPlansGetParametersQueryStatus.Value1;

interface UserPlansTabProps {
  user: User;
  plansState: UserPlansState;
  plansActions: UserPlansActions;
  mutationActions: UserPlanMutationsActions;
  isDeleting: boolean;
  onCreatePlan: () => void;
  onEditPlan: (plan: Plan) => void;
}

export const UserPlansTab: React.FC<UserPlansTabProps> = ({
  user,
  plansState,
  plansActions,
  mutationActions,
  isDeleting,
  onCreatePlan,
  onEditPlan,
}) => {
  const { plans, total, totalPages, isLoading, filters } = plansState;

  const getStatusBadge = (status: PlanStatus | undefined) => {
    if (status === PLAN_STATUS_COMPLETED) {
      return (
        <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800'>
          Completed
        </span>
      );
    }
    return (
      <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800'>
        Active
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className='p-6'>
        <div className='animate-pulse space-y-4'>
          <div className='h-10 bg-gray-200 rounded w-32'></div>
          <div className='h-8 bg-gray-200 rounded'></div>
          <div className='h-16 bg-gray-200 rounded'></div>
          <div className='h-16 bg-gray-200 rounded'></div>
          <div className='h-16 bg-gray-200 rounded'></div>
        </div>
      </div>
    );
  }

  return (
    <div className='p-6 space-y-4'>
      {/* Header with Create Button */}
      <div className='flex items-center justify-between'>
        <div className='text-sm text-gray-600'>
          {total} plan{total !== 1 ? 's' : ''} found
        </div>
        <Button
          icon={mdiPlus}
          label='Create Plan'
          color='info'
          small
          onClick={onCreatePlan}
        />
      </div>

      {/* Filters */}
      <div className='flex items-center space-x-4'>
        <select
          value={filters.status ?? ''}
          onChange={e =>
            plansActions.setStatus(
              e.target.value === ''
                ? undefined
                : (Number(
                    e.target.value,
                  ) as PathsAdminUsersUserIdPlansGetParametersQueryStatus),
            )
          }
          className='text-sm border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500'
        >
          <option value=''>All Statuses</option>
          <option value={QUERY_STATUS_ACTIVE}>Active</option>
          <option value={QUERY_STATUS_COMPLETED}>Completed</option>
        </select>

        <select
          value={filters.sortBy}
          onChange={e =>
            plansActions.setSortBy(
              e.target.value as 'startAt' | 'endAt' | 'createdAt' | 'status',
            )
          }
          className='text-sm border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500'
        >
          <option value='startAt'>Sort by Start Date</option>
          <option value='endAt'>Sort by End Date</option>
          <option value='createdAt'>Sort by Created</option>
          <option value='status'>Sort by Status</option>
        </select>

        <select
          value={filters.sortOrder}
          onChange={e =>
            plansActions.setSortOrder(e.target.value as 'asc' | 'desc')
          }
          className='text-sm border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500'
        >
          <option value='desc'>Newest First</option>
          <option value='asc'>Oldest First</option>
        </select>
      </div>

      {/* Plans List */}
      {plans.length === 0 ? (
        <div className='text-center py-12'>
          <p className='text-gray-500'>No plans found for this user.</p>
          <Button
            icon={mdiPlus}
            label='Create First Plan'
            color='info'
            className='mt-4'
            onClick={onCreatePlan}
          />
        </div>
      ) : (
        <div className='space-y-3'>
          {plans.map(plan => (
            <div
              key={plan.id}
              className='p-4 rounded-lg border border-gray-200 bg-white hover:shadow-md transition-shadow'
            >
              <div className='flex items-start justify-between'>
                <div className='flex-1'>
                  {/* Business Info */}
                  <div className='flex items-center space-x-3 mb-2'>
                    <div className='w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white text-sm font-semibold'>
                      {plan.business?.name?.[0]?.toUpperCase() || 'B'}
                    </div>
                    <div>
                      <p className='font-medium text-gray-900'>
                        {plan.business?.name || 'Unknown Business'}
                      </p>
                      <p className='text-xs text-gray-500'>
                        {plan.business?.address}, {plan.business?.city}
                      </p>
                    </div>
                  </div>

                  {/* Plan Details */}
                  <div className='grid grid-cols-2 gap-4 mt-3'>
                    <div>
                      <dt className='text-xs text-gray-500'>Start</dt>
                      <dd className='text-sm text-gray-900'>
                        {formatDateWithYear(plan.startAt)}
                      </dd>
                    </div>
                    <div>
                      <dt className='text-xs text-gray-500'>End</dt>
                      <dd className='text-sm text-gray-900'>
                        {formatDateWithYear(plan.endAt)}
                      </dd>
                    </div>
                    {plan.checkInAt && (
                      <div>
                        <dt className='text-xs text-gray-500'>Check-in</dt>
                        <dd className='text-sm text-gray-900'>
                          {formatDateWithYear(plan.checkInAt)}
                        </dd>
                      </div>
                    )}
                    {plan.checkOutAt && (
                      <div>
                        <dt className='text-xs text-gray-500'>Check-out</dt>
                        <dd className='text-sm text-gray-900'>
                          {formatDateWithYear(plan.checkOutAt)}
                        </dd>
                      </div>
                    )}
                  </div>
                </div>

                {/* Status and Actions */}
                <div className='ml-4 flex flex-col items-end space-y-2'>
                  {getStatusBadge(plan.status)}

                  <div className='flex items-center space-x-1 mt-2'>
                    <Button
                      icon={mdiPencil}
                      color='info'
                      small
                      outline
                      onClick={() => onEditPlan(plan)}
                    />
                    <Button
                      icon={mdiDelete}
                      color='danger'
                      small
                      outline
                      disabled={isDeleting}
                      onClick={() =>
                        mutationActions.handleDeletePlan(user.id!, plan)
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={filters.page}
          totalPages={totalPages}
          onPageChange={plansActions.setPage}
          totalItems={total}
          itemsPerPage={filters.limit}
          className='mt-4'
        />
      )}
    </div>
  );
};
